import { Link, useForm } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'

import Tracker from '../../components/Tracker'
import { ArrowLeftIcon, CheckIcon, ChevronDownIcon, ImagePlaceholderIcon } from '../../components/Icons'
import { ModelViewer } from '@/components/ModelViewer'
import { DEFAULT_STAMP_STYLE, StampStep } from '@/components/StampStep'
import { StoreImage } from '@/components/StoreImage'
import { trackEvent } from '@/lib/track'
import { useIsMini, useMediaQuery } from '@/lib/useMediaQuery'
import { useCustomizeDraft, type StampStyle } from '@/lib/useCustomizeDraft'
import { cn } from '@/lib/utils'
import { Chip } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/label'
import { Input, NumericInput } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/select'
import { OptionCard, RadioGroup } from '@/components/ui/radio-group'
import { ResponsiveDialog } from '@/components/ui/responsive-dialog'
import { StepRail } from '@/components/ui/step-rail'

interface ProductImage {
  id: number
  url: string
}

interface Product {
  id: number
  name: string
  description: string | null
  material: string | null
  tag: string | null
  price: number
  inStock: boolean
  oneOfOne: boolean
  images: ProductImage[]
  /** Web path to an optimized GLB; null means this piece has photos only. */
  modelUrl: string | null
}

interface StepOption {
  key: string
  label: string
  description: string | null
  priceDelta: number
  priceLabel: string
  available: boolean
  imageUrl: string | null
}

interface Step {
  key: string
  kind: 'selection' | 'engraving'
  title: string
  label: string
  hint: string | null
  required: boolean
  charLimit: number
  placeholder: string | null
  imageUrl: string | null
  options: StepOption[]
}

interface ShippingCountry {
  code: string
  name: string
}

interface Props {
  product: Product
  steps: Step[]
  countries: ShippingCountry[]
}

/** Sentinel for "no engraving" on an optional engraving step. */
const NO_TEXT = '__none__'

type CustomizeForm = {
  /** Step key → chosen option key, or the engraving text. */
  selections: Record<string, string>
  /** Step key → the font/size/colour that engraving is cut in. */
  stamp_styles: Record<string, StampStyle>
  customer_name: string
  customer_email: string
  shipping_country: string
  shipping_region: string
  shipping_city: string
  shipping_postal_code: string
  shipping_address: string
  card_name: string
  card_number: string
  card_exp: string
  card_cvc: string
}

/**
 * v2 · 07 Customize — a pushed page with a step header. No more
 * bottom sheet: every viewport gets the same anatomy — 56px header
 * (back · step title · n/N), segmented progress bar, the piece in a
 * bordered frame, then one step at a time with a white block CTA.
 * Desktop widens into the 1.1/1 split with the media on the left.
 *
 * Every step, its copy, its image and the price bump behind each
 * choice comes from the database, so a new configurable product is a
 * data change rather than a code change. The build is mirrored to
 * localStorage so it survives a reload.
 */
export default function ProductsCustomize({ product, steps, countries }: Props) {
  const isMini = useIsMini()
  // The media column is wide-and-short stacked, tall-and-narrow beside
  // the steps; model-viewer frames a bounding sphere without regard for
  // either, so the pull-in is keyed to the stop the grid switches on.
  const stacked = useMediaQuery('(max-width: 767px)')
  const payIndex = steps.length
  const [step, setStep] = useState(0)

  // `selections` is a nested map keyed by step, so the form's value
  // type has to admit it alongside the flat string fields.
  const { data, setData, post, processing, errors } = useForm<CustomizeForm>({
    selections: {},
    stamp_styles: {},
    customer_name: '',
    customer_email: '',
    shipping_country: '',
    shipping_region: '',
    shipping_city: '',
    shipping_postal_code: '',
    shipping_address: '',
    card_name: '',
    card_number: '',
    card_exp: '',
    card_cvc: '',
  })

  const selections = data.selections
  const stampStyles = data.stamp_styles
  const setSelection = (key: string, value: string) =>
    setData((current) => ({
      ...current,
      selections: { ...current.selections, [key]: value },
    }))

  /** An engraving commits its text and its styling together. */
  const setStamp = (key: string, value: string, style: StampStyle) =>
    setData((current) => ({
      ...current,
      selections: { ...current.selections, [key]: value },
      stamp_styles: { ...current.stamp_styles, [key]: style },
    }))

  const { draft, ready, save, clear } = useCustomizeDraft(product.id)
  const [restored, setRestored] = useState(false)

  // Restore the saved build, then open the write path. Nothing is
  // written before this runs, or the mount-time save would overwrite
  // the draft it is about to read.
  useEffect(() => {
    if (!ready || restored) return
    if (draft) {
      // Drop selections for steps that no longer exist — the merchant
      // may have reshaped the configurator since the draft was saved.
      const live = Object.fromEntries(
        Object.entries(draft.selections).filter(([key]) => steps.some((s) => s.key === key))
      )
      const liveStyles = Object.fromEntries(
        Object.entries(draft.stampStyles ?? {}).filter(([key]) => steps.some((s) => s.key === key))
      )
      setData((current) => ({ ...current, selections: live, stamp_styles: liveStyles }))
      setStep(Math.min(draft.step, payIndex))
    }
    setRestored(true)
    // setData is stable for the life of the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, draft, restored, payIndex])

  useEffect(() => {
    if (!restored) return
    save({ step, selections, stampStyles })
  }, [restored, step, selections, stampStyles, save])

  /** A step is answered when it holds a usable value. */
  const isAnswered = (target: Step): boolean => {
    const value = selections[target.key]
    if (target.kind === 'engraving') {
      if (value === NO_TEXT) return !target.required
      return !!value?.trim()
    }
    return !!value && target.options.some((o) => o.key === value && o.available)
  }

  const buildComplete = steps.every((s) => !s.required || isAnswered(s))

  const chosen = useMemo(
    () =>
      steps.flatMap((s) => {
        const value = selections[s.key]
        if (!value || value === NO_TEXT) return []
        if (s.kind === 'engraving') {
          return [{ step: s, label: `“${value.toUpperCase()}”`, delta: 0, image: null as string | null }]
        }
        const option = s.options.find((o) => o.key === value)
        return option
          ? [{ step: s, label: option.label, delta: option.priceDelta, image: option.imageUrl }]
          : []
      }),
    [steps, selections]
  )

  // Base price plus every chosen bump. The server recomputes this from
  // its own records at checkout; this is only what the customer sees.
  const total = product.price + chosen.reduce((sum, entry) => sum + entry.delta, 0)
  const totalLabel = `$${total.toFixed(2)}`

  const rail = [...steps.map((s) => s.label), 'Pay']
  const atPay = step === payIndex

  const baseError = formatError((errors as Record<string, string | string[]>).base)

  const [outOfStock, setOutOfStock] = useState(false)
  const [leadSaved, setLeadSaved] = useState(false)

  const captureLead = () => {
    if (leadSaved || !data.customer_email.trim()) return
    setLeadSaved(true)
    // Fire-and-forget, like the tracking beacon (endpoint is CSRF-exempt).
    fetch('/customize/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        product_id: product.id,
        customer_name: data.customer_name,
        customer_email: data.customer_email,
        shipping_country: data.shipping_country,
        shipping_region: data.shipping_region,
        shipping_city: data.shipping_city,
        shipping_postal_code: data.shipping_postal_code,
        shipping_address: data.shipping_address,
        selections,
      }),
    }).catch(() => {
      // Never let a failed save block the sold-out message.
    })
  }

  /** Fires when the shopper focuses any card field. */
  const handleCardEngage = () => {
    if (outOfStock) return
    trackEvent('form_field', {
      label: 'card',
      meta: { total, country: data.shipping_country || null },
      stage: 'filled_details',
    })
    captureLead()
    setOutOfStock(true)
  }

  const goToPayment = () => {
    if (!buildComplete) return
    trackEvent('begin_checkout', {
      label: product.name,
      meta: { total, selections },
      stage: 'reached_checkout',
    })
    setStep(payIndex)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Out of stock for now: card submission is intercepted at the modal.
    if (outOfStock) {
      setOutOfStock(true)
      return
    }
    trackEvent('place_order', {
      label: totalLabel,
      meta: {
        total,
        country: data.shipping_country || null,
        city: data.shipping_city || null,
      },
    })
    // The build is committed server-side now; the draft has done its job.
    clear()
    post('/customize')
  }

  const activeStep: Step | undefined = steps[step]
  // The most recently chosen option's art wins, then the step's own,
  // then the product cover.
  const previewImage =
    [...chosen].reverse().find((entry) => entry.image)?.image ??
    activeStep?.imageUrl ??
    product.images[0]?.url ??
    null

  const chips = (
    <>
      {chosen.map((entry) => (
        <Chip key={entry.step.key} variant={entry.step.kind === 'engraving' ? 'ink' : 'default'}>
          {entry.label}
        </Chip>
      ))}
    </>
  )

  const soldOutModal = (
    <ResponsiveDialog
      open={outOfStock}
      onOpenChange={setOutOfStock}
      title="This one's gone"
      description={`${product.name} is made to order and we're temporarily out of stock. We've saved your details and will email you first when it's back — no charge was made.`}
      cancel={{ label: 'Not now', onClick: () => setOutOfStock(false) }}
    />
  )

  // Continue is owed only the current step's answer, and the last
  // content step hands over to payment.
  const continueDisabled = !atPay && !!activeStep?.required && !isAnswered(activeStep)
  const advance = () => {
    if (step < payIndex - 1) setStep(step + 1)
    else goToPayment()
  }

  // A piece with a scanned model is shown in 3D; everything else keeps
  // the photo, with the most recently chosen option's art winning.
  const media = product.modelUrl ? (
    <ModelViewer
      src={product.modelUrl}
      alt={`${product.name} — interactive 3D view`}
      // Sized for the widest point of the auto-rotation: a rectangular
      // object is ~1.6× wider corner-on than face-on, and framing for
      // the narrow view clips it half the time.
      cameraOrbit={stacked ? '25deg 66deg 86%' : '25deg 62deg 82%'}
    />
  ) : previewImage ? (
    <StoreImage src={previewImage} alt={product.name} className="size-full object-cover" />
  ) : (
    <span className="flex size-full items-center justify-center text-ink-faint">
      <ImagePlaceholderIcon size={96} />
    </span>
  )

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Tracker />

      {/* Step header — back · title · n/N. */}
      <header className="flex h-14 shrink-0 items-center px-3">
        {step > 0 ? (
          <button
            type="button"
            aria-label="Back"
            onClick={() => setStep(step - 1)}
            className="flex size-11 cursor-pointer items-center justify-center text-ink-muted hover:text-ink"
          >
            <ArrowLeftIcon size={18} />
          </button>
        ) : (
          <Link
            href={`/products/${product.id}`}
            aria-label="Back"
            className="flex size-11 items-center justify-center text-ink-muted hover:text-ink"
          >
            <ArrowLeftIcon size={18} />
          </Link>
        )}
        <span className="flex-1 text-center text-[16px] font-semibold">
          {atPay ? 'Checkout' : activeStep?.label ?? 'Customize'}
        </span>
        <span className="flex size-11 items-center justify-center text-[13px] text-ink-faint tabular-nums">
          {Math.min(step + 1, rail.length)}/{rail.length}
        </span>
      </header>

      {/* Segmented progress under the header. */}
      <div className="border-b border-hairline px-4 pb-3.5">
        <StepRail steps={rail} current={step} />
      </div>

      <div className="mx-auto grid w-full max-w-[1040px] flex-1 grid-rows-[minmax(280px,1fr)_auto] gap-5 px-4 py-4 md:grid-cols-[1.1fr_1fr] md:grid-rows-1 md:gap-12 md:px-8 md:py-10">
        {/* The live build — the frame soaks up every spare pixel, so
            the piece is as large as the viewport allows.

            A 3D preview goes full-bleed on mobile: an interactive scene
            is the whole point of the page, and a card edge around it
            just steals width from the model. Desktop keeps the frame,
            where the media sits beside the steps rather than above. */}
        <div
          className={cn(
            // Stays a stretched grid item: model-viewer sizes at 100% of
            // this box, so anything that lets it collapse (self-start,
            // auto height) renders an empty frame.
            'min-h-0 overflow-hidden md:min-h-[560px] md:rounded-[20px] md:border md:border-hairline',
            product.modelUrl
              ? '-mx-4 bg-background md:mx-0 md:bg-surface'
              : 'rounded-[18px] border border-hairline bg-surface'
          )}
        >
          {media}
        </div>

        {/* One step at a time, CTA pinned to the column's bottom. */}
        <div className="flex min-h-[320px] flex-col md:pt-2">
          {atPay ? (
            <PayPanel
              product={product}
              total={total}
              data={data}
              setData={(key, value) => setData(key, value)}
              countries={countries}
              errors={errors as Record<string, string | string[]>}
              baseError={baseError}
              processing={processing}
              outOfStock={outOfStock}
              onCardEngage={handleCardEngage}
              onSubmit={handleSubmit}
              chips={chips}
              compact={isMini}
            />
          ) : activeStep ? (
            <>
              <h1 className="text-[22px] font-bold tracking-[-0.02em] md:text-[32px] md:tracking-[-0.03em]">
                {activeStep.title}
              </h1>
              {activeStep.hint && (
                <p className="mt-1.5 text-[14px] leading-relaxed text-ink-muted md:text-[16px]">
                  {activeStep.hint}
                </p>
              )}
              <div className="mt-4 md:mt-6">
                <StepBody
                  step={activeStep}
                  value={selections[activeStep.key] ?? ''}
                  stampStyle={stampStyles[activeStep.key] ?? DEFAULT_STAMP_STYLE}
                  // Picking an option only answers the step — Continue
                  // is the one thing that moves the flow forward.
                  onChange={(value) => setSelection(activeStep.key, value)}
                  onStampChange={(value, style) => setStamp(activeStep.key, value, style)}
                  look="card"
                />
              </div>
              {steps.length === 0 && (
                <p className="text-[14px] text-ink-muted">
                  This piece has no configurator steps yet.
                </p>
              )}

              <div className="mt-auto pt-6">
                <div className="mb-3 flex items-baseline justify-between">
                  <span className="text-[14px] text-ink-muted">Total</span>
                  <span className="text-[20px] font-bold tabular-nums">{totalLabel}</span>
                </div>
                <Button
                  variant="primary"
                  size="block"
                  disabled={continueDisabled}
                  onClick={advance}
                  className="mb-4"
                >
                  {step === payIndex - 1 ? 'Continue to payment' : 'Continue'}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {soldOutModal}
    </div>
  )
}

/* --------------------------------------------------------------- steps ---- */

/** A step renders as a list of choices, or as an engraving. */
function StepBody({
  step,
  value,
  stampStyle,
  onChange,
  onStampChange,
  look,
}: {
  step: Step
  value: string
  stampStyle: StampStyle
  onChange: (value: string) => void
  onStampChange: (value: string, style: StampStyle) => void
  look: 'card' | 'row'
}) {
  if (step.kind === 'engraving') {
    return (
      <StampStep
        stepKey={step.key}
        title={step.title}
        hint={step.hint}
        charLimit={step.charLimit}
        placeholder={step.placeholder}
        required={step.required}
        value={value}
        style={stampStyle}
        onChange={onStampChange}
        noTextValue={NO_TEXT}
      />
    )
  }

  return (
    <RadioGroup value={value} onValueChange={onChange} className="gap-2.5">
      {step.options.map((option) => (
        <OptionCard
          key={option.key}
          value={option.key}
          look={look}
          disabled={!option.available}
          title={option.label}
          meta={option.available ? option.description : 'Out of stock'}
          // Only surcharges earn a price label — stamping "Included" on
          // every base option is noise on the one row that matters.
          price={option.priceDelta !== 0 ? option.priceLabel : undefined}
        />
      ))}
    </RadioGroup>
  )
}

/* ----------------------------------------------------------------- pay ---- */

/** Only the flat delivery/card half of the form reaches the pay panel. */
type PayFields = Omit<CustomizeForm, 'selections' | 'stamp_styles'>

interface PayProps {
  product: Product
  total: number
  data: PayFields
  setData: (key: keyof PayFields, value: string) => void
  countries: ShippingCountry[]
  errors: Record<string, string | string[]>
  baseError?: string
  processing: boolean
  outOfStock: boolean
  onCardEngage: () => void
  onSubmit: (e: React.FormEvent) => void
  chips: React.ReactNode
  compact?: boolean
}

function PayPanel({
  product,
  total,
  data,
  setData,
  countries,
  errors,
  baseError,
  processing,
  outOfStock,
  onCardEngage,
  onSubmit,
  chips,
  compact,
}: PayProps) {
  // Progressive disclosure: Delivery unfolds once Contact is complete,
  // Card once Delivery is. A header tap overrides either way, and a
  // group with server errors always opens so the message is visible.
  const [overrides, setOverrides] = useState<Record<string, boolean | undefined>>({})
  const toggle = (key: string, current: boolean) =>
    setOverrides((prev) => ({ ...prev, [key]: !current }))

  const hasError = (keys: string[]) => keys.some((key) => errors[key])
  const contactDone = !!data.customer_name.trim() && /\S+@\S+\.\S+/.test(data.customer_email)
  const deliveryDone =
    !!data.shipping_country && !!data.shipping_city.trim() && !!data.shipping_address.trim()

  const openContact = overrides.contact ?? true
  const openDelivery =
    overrides.delivery ??
    (contactDone || hasError(['shipping_country', 'shipping_city', 'shipping_region', 'shipping_postal_code', 'shipping_address']))
  const openCard =
    overrides.card ??
    ((contactDone && deliveryDone) || hasError(['card_name', 'card_number', 'card_exp', 'card_cvc']))

  return (
    <form onSubmit={onSubmit} className="flex flex-1 flex-col">
      <h1 className="text-[22px] font-bold tracking-[-0.02em] md:text-[28px]">
        Delivery &amp; payment
      </h1>

      {/* Build recap — the piece, its price, and the spec chips. */}
      <div className="mb-5 mt-4 rounded-lg border border-hairline bg-surface p-4">
        <div className="flex justify-between gap-3">
          <span className="text-[15px] font-semibold">{product.name}</span>
          <span className="text-[15px] font-semibold tabular-nums">${total.toFixed(2)}</span>
        </div>
        <div className="mt-2.5 flex flex-wrap gap-2">{chips}</div>
      </div>

      {baseError && (
        <div
          role="alert"
          className="mb-4 flex items-center gap-3 rounded-md border border-destructive bg-surface px-4 py-3 text-[14px] text-destructive"
        >
          <span className="size-1.5 shrink-0 rounded-full bg-destructive" />
          {baseError}
        </div>
      )}

      <div className="flex flex-col gap-3">
        <FormGroup
          title="Contact"
          open={openContact}
          complete={contactDone}
          onToggle={() => toggle('contact', openContact)}
        >
          <TextField
            id="customer_name"
            label="Name"
            required
            value={data.customer_name}
            onChange={(v) => setData('customer_name', v)}
            error={errors.customer_name}
          />
          <TextField
            id="customer_email"
            label="Email"
            type="email"
            required
            value={data.customer_email}
            onChange={(v) => setData('customer_email', v)}
            error={errors.customer_email}
          />
        </FormGroup>

        <FormGroup
          title="Delivery"
          open={openDelivery}
          complete={deliveryDone}
          onToggle={() => toggle('delivery', openDelivery)}
        >
          <Field
            label="Country"
            htmlFor="shipping_country"
            required
            error={formatError(errors.shipping_country)}
          >
            <NativeSelect
              id="shipping_country"
              value={data.shipping_country}
              required
              aria-invalid={errors.shipping_country ? true : undefined}
              onChange={(e) => setData('shipping_country', e.target.value)}
            >
              <option value="" disabled>
                —
              </option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </NativeSelect>
          </Field>

          <div className={cn('grid gap-3', compact ? 'grid-cols-1' : 'grid-cols-2')}>
            <TextField
              id="shipping_city"
              label="City"
              required
              value={data.shipping_city}
              onChange={(v) => setData('shipping_city', v)}
              error={errors.shipping_city}
            />
            <TextField
              id="shipping_region"
              label="Region / State"
              value={data.shipping_region}
              onChange={(v) => setData('shipping_region', v)}
              error={errors.shipping_region}
            />
          </div>
          <div className={cn('grid gap-3', compact ? 'grid-cols-1' : 'grid-cols-2')}>
            <TextField
              id="shipping_postal_code"
              label="ZIP"
              value={data.shipping_postal_code}
              onChange={(v) => setData('shipping_postal_code', v)}
              error={errors.shipping_postal_code}
            />
            <TextField
              id="shipping_address"
              label="Address"
              required
              value={data.shipping_address}
              onChange={(v) => setData('shipping_address', v)}
              error={errors.shipping_address}
            />
          </div>
        </FormGroup>

        <FormGroup
          title="Card"
          open={openCard}
          complete={false}
          onToggle={() => toggle('card', openCard)}
        >
          {/* Focusing any card field triggers the sold-out capture. */}
          <fieldset
            disabled={outOfStock}
            onFocusCapture={onCardEngage}
            className="flex flex-col gap-4 border-0 p-0"
          >
            <TextField
              id="card_name"
              label="Name on card"
              value={data.card_name}
              onChange={(v) => setData('card_name', v)}
              error={errors.card_name}
            />
            <Field label="Card number" htmlFor="card_number" error={formatError(errors.card_number)}>
              <NumericInput
                id="card_number"
                value={data.card_number}
                maxLength={23}
                placeholder="4242 4242 4242 4242"
                aria-invalid={errors.card_number ? true : undefined}
                onChange={(e) => setData('card_number', formatCardNumber(e.target.value))}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Expiry" htmlFor="card_exp" error={formatError(errors.card_exp)}>
                <NumericInput
                  id="card_exp"
                  value={data.card_exp}
                  maxLength={5}
                  placeholder="MM/YY"
                  aria-invalid={errors.card_exp ? true : undefined}
                  onChange={(e) => setData('card_exp', formatExpiry(e.target.value))}
                />
              </Field>
              <Field label="CVC" htmlFor="card_cvc" error={formatError(errors.card_cvc)}>
                <NumericInput
                  id="card_cvc"
                  value={data.card_cvc}
                  maxLength={4}
                  placeholder="123"
                  aria-invalid={errors.card_cvc ? true : undefined}
                  onChange={(e) => setData('card_cvc', e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </Field>
            </div>
          </fieldset>
        </FormGroup>
      </div>

      {outOfStock && (
        <div
          role="status"
          className="mt-5 rounded-md border border-[color:var(--sss-accent-tint-line)] bg-signal-tint px-4 py-3 text-[14px] text-signal"
        >
          Out of stock — we’ve saved your details and will email you when it’s back.
        </div>
      )}

      {/* No separate total row — the price lives on the button. */}
      <div className="mt-auto pt-6">
        <Button
          type="submit"
          variant="primary"
          size="block"
          processing={processing}
          disabled={outOfStock}
          className="mb-2.5"
        >
          {processing ? 'Placing order…' : `Pay $${total.toFixed(2)}`}
        </Button>
        <p className="mb-4 text-center text-[13px] leading-snug text-ink-faint">
          Your card is charged when the piece ships. Free returns within 14 days on
          non-personalised builds.
        </p>
      </div>
    </form>
  )
}

/**
 * Collapsible checkout group — a bordered card whose header shows a
 * pink check once the group is complete. Groups unfold on their own
 * as the previous one is filled in; the header always toggles.
 */
function FormGroup({
  title,
  open,
  complete,
  onToggle,
  children,
}: {
  title: string
  open: boolean
  complete: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <section className="rounded-lg border border-hairline bg-surface">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-4 text-left"
      >
        <span className="flex items-center gap-2.5 text-[15px] font-semibold">
          {title}
          {complete && <CheckIcon size={16} className="text-signal" />}
        </span>
        <ChevronDownIcon
          size={18}
          className={cn(
            'text-ink-faint transition-transform duration-[200ms] ease-standard',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <div className="flex flex-col gap-4 border-t border-hairline p-4">{children}</div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------- helpers ---- */

function TextField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required,
  error,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  required?: boolean
  error?: string | string[]
}) {
  const message = formatError(error)
  return (
    <Field label={label} htmlFor={id} required={required} error={message}>
      <Input
        id={id}
        type={type}
        value={value}
        required={required}
        aria-invalid={message ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  )
}

function formatError(error?: string | string[]): string | undefined {
  if (!error) return undefined
  return Array.isArray(error) ? error.join(', ') : error
}

function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 19)
  return digits.replace(/(.{4})/g, '$1 ').trim()
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}/${digits.slice(2)}`
}
